import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from '@nextgen-web-framework/all';
import { ShopByCategory, transformComponent } from '@commerce-webcomponents/ui';
import {
  SearchState,
  BucketField,
  BucketValuesInner,
  CmsData,
  FeaturedCardItem,
  FeaturedCards,
} from '@commerce-search-webcomponents/sdk';
import { BrandsAndSolutionsProps } from './types/propTypes';
import styles from './BrandsAndSolutions.module.scss';

const BrandsAndSolutions = ({ buckets, showPopular }: BrandsAndSolutionsProps) => {
  const [popularData, setPopularData] = useState<CmsData>({});
  const [matchedData, setMatchedData] = useState<CmsData>({});
  const { cmsData } = useSelector((state: { commerceSearch: SearchState }) => state.commerceSearch);
  const t = useTranslation();

  useEffect(() => {
    const { shop_by_brands, shop_by_category } = cmsData;
    const brands = shop_by_brands?.featured_cards?.[0].items?.filter((value) => value.popular);
    const solutions = shop_by_category?.featured_cards?.[0].items?.filter((value) => value.popular);
    setPopularData({
      shop_by_brands: getFeaturedCardsObject(shop_by_brands, brands),
      shop_by_category: getFeaturedCardsObject(shop_by_category, solutions),
    });
  }, [cmsData]);

  useEffect(() => {
    const { BRAND, SOLUTION } = BucketField;
    const _buckets = buckets?.filter(({ field }) => field === SOLUTION || field === BRAND);
    const data: CmsData = {};
    const { shop_by_brands, shop_by_category } = cmsData;

    _buckets?.forEach(({ field, values }) => {
      if (field === SOLUTION && values) {
        data.shop_by_category = getMatchedFeaturedCards(values, shop_by_category);
      } else if (field === BRAND && values) {
        data.shop_by_brands = getMatchedFeaturedCards(values, shop_by_brands);
      }
    });
    setMatchedData(data);
  }, [buckets]);

  useEffect(() => {
    if (showPopular) {
      setMatchedData({});
    }
  }, [showPopular]);

  const getMatchedFeaturedCards = (values: BucketValuesInner[], obj?: FeaturedCards) => {
    const ids = values.map(({ name }) => name);
    const uniqueIds = new Set<string>();
    const items = obj?.featured_cards?.[0].items?.filter(({ item_id }) => {
      const value = ids.includes(item_id) && !uniqueIds.has(item_id);
      uniqueIds.add(item_id);
      return value;
    });
    return getFeaturedCardsObject(obj, items);
  };

  const getFeaturedCardsObject = (obj?: FeaturedCards, items?: FeaturedCardItem[]) => {
    if (obj && items?.length) {
      return {
        ...obj,
        featured_cards: [
          {
            ...obj.featured_cards?.[0],
            items,
          },
        ],
      };
    }
    return undefined;
  };

  const solutionsData = matchedData.shop_by_category || popularData.shop_by_category;
  const brandsData = matchedData.shop_by_brands || popularData.shop_by_brands;
  const commonConfig = { hideHeader: true, containerClassName: styles['featured-cards-container'] };
  return (
    <>
      {solutionsData && (
        <div className={styles['solutions-section']}>
          <div className={styles['header-title']}>
            {t(matchedData.shop_by_category ? 'search.searchOverlay.solutions' : 'search.searchOverlay.topSolutions')}
          </div>
          <ShopByCategory {...transformComponent(solutionsData, 'featured_cards')} {...commonConfig} />
        </div>
      )}
      {brandsData && (
        <div className={styles['brands-section']}>
          <div className={styles['header-title']}>
            {t(matchedData.shop_by_brands ? 'search.searchOverlay.brands' : 'search.searchOverlay.topBrands')}
          </div>
          <ShopByCategory {...transformComponent(brandsData, 'featured_cards')} {...commonConfig} />
        </div>
      )}
    </>
  );
};

export default BrandsAndSolutions;
